// The site's five interface languages — one record each (unify-ui ship 3, spec 2026-10-10 §Ship 3;
// addresses ruling R13). Pure data and pure functions only: importing this file touches no DOM, so
// the build (node), the pages and the checks all read the same list and never drift apart.
//   id      the language's own id — also the stats beacon value and a table file's name
//   prefix  the address prefix its pages live under; '' is English at today's addresses
//   lang    the <html lang> value the build writes (BCP 47 script tags for Chinese)
//   own     the language's name in itself — the one word a switch entry shows
//   locale  the ICU locale for number grouping on that language's pages
//   og      the og:locale code of its heads
//   short   the code the switch shows at the phone layout (R70: an icon and this; the menu names all five in full)
//   stack   the interface font stacks its pages draw text with. Latin + Thai ride the self-hosted
//           faces (ruling R8); ja/zh pages use device faces for CJK — the lists here are what
//           filasophy.css's :lang() rules set, and a test holds the two to the same words.
export const LANGUAGES = [
  {
    id: 'en', prefix: '', lang: 'en', own: 'English', locale: 'en', og: 'en_US', short: 'EN',
    stack: {
      display: '"Sora","Prompt","Noto Sans Thai","Segoe UI",system-ui,sans-serif',
      body: '"Manrope","Prompt","Noto Sans Thai","Segoe UI",system-ui,sans-serif',
      mono: '"JetBrains Mono","Prompt","Noto Sans Thai",ui-monospace,"SF Mono",monospace',
    },
  },
  {
    id: 'th', prefix: '/th', lang: 'th', own: 'ไทย', locale: 'th-TH', og: 'th_TH', short: 'TH',
    stack: {
      display: '"Sora","Prompt","Noto Sans Thai","Segoe UI",system-ui,sans-serif',
      body: '"Manrope","Prompt","Noto Sans Thai","Segoe UI",system-ui,sans-serif',
      mono: '"JetBrains Mono","Prompt","Noto Sans Thai",ui-monospace,"SF Mono",monospace',
    },
  },
  {
    id: 'ja', prefix: '/ja', lang: 'ja', own: '日本語', locale: 'ja', og: 'ja_JP', short: 'JA',
    stack: {
      display: '"Sora","Hiragino Kaku Gothic ProN","Yu Gothic","Noto Sans JP","Segoe UI",system-ui,sans-serif',
      body: '"Manrope","Hiragino Kaku Gothic ProN","Yu Gothic","Noto Sans JP","Segoe UI",system-ui,sans-serif',
      mono: '"JetBrains Mono","Hiragino Kaku Gothic ProN","Yu Gothic",ui-monospace,"SF Mono",monospace',
    },
  },
  {
    id: 'zh-cn', prefix: '/zh-cn', lang: 'zh-Hans', own: '简体中文', locale: 'zh-CN', og: 'zh_CN', short: '简',
    stack: {
      display: '"Sora","PingFang SC","Microsoft YaHei","Noto Sans SC","Segoe UI",system-ui,sans-serif',
      body: '"Manrope","PingFang SC","Microsoft YaHei","Noto Sans SC","Segoe UI",system-ui,sans-serif',
      mono: '"JetBrains Mono","PingFang SC","Microsoft YaHei",ui-monospace,"SF Mono",monospace',
    },
  },
  {
    id: 'zh-tw', prefix: '/zh-tw', lang: 'zh-Hant', own: '繁體中文', locale: 'zh-TW', og: 'zh_TW', short: '繁',
    stack: {
      display: '"Sora","PingFang TC","Microsoft JhengHei","Noto Sans TC","Segoe UI",system-ui,sans-serif',
      body: '"Manrope","PingFang TC","Microsoft JhengHei","Noto Sans TC","Segoe UI",system-ui,sans-serif',
      mono: '"JetBrains Mono","PingFang TC","Microsoft JhengHei",ui-monospace,"SF Mono",monospace',
    },
  },
];

export const byId = Object.fromEntries(LANGUAGES.map((l) => [l.id, l]));
export const byLang = Object.fromEntries(LANGUAGES.map((l) => [l.lang, l]));
export const byPrefix = Object.fromEntries(LANGUAGES.map((l) => [l.prefix, l]));

// The page's own language, from <html lang> and nothing else (spec: the i18n row of the package
// table). An unknown value cannot come from a built page — the build writes it — so it is reported
// loudly once and the page carries on in English rather than dying half-loaded.
let reportedUnknown = false;
export function pageLanguage() {
  if (typeof document === 'undefined') return byId.en;
  const value = (document.documentElement && document.documentElement.lang) || '';
  const hit = byLang[value];
  if (!hit) {
    if (!reportedUnknown) { reportedUnknown = true; console.error(`locales: unknown <html lang> "${value}" — serving English`); }
    return byId.en;
  }
  return hit;
}

// The same page's address in another language: the tool keeps its own path and design, the prefix
// carries the language (R13). `toolPath` is the registry path ('/name-tag/', '/' for the hub);
// `hash` is the design a share link holds ('s=…'), carried across as it is.
export function hrefFor(languageId, toolPath, hash) {
  const language = byId[languageId];
  if (!language) throw new Error(`locales: unknown language id "${languageId}"`);
  if (typeof toolPath !== 'string' || !toolPath.startsWith('/')) throw new Error(`locales: toolPath "${toolPath}" is not a site path`);
  const path = toolPath === '/' ? '/' : (toolPath.endsWith('/') ? toolPath : `${toolPath}/`);
  return `${language.prefix}${path}${hash ? `#${hash.replace(/^#/, '')}` : ''}`;
}
